import type { MouseEvent, PointerEvent } from 'react';
import { usePointerActivation } from '@/hooks';
import {
	X,
	LogOut,
	Clock,
	Instagram,
	FileText,
	Shield,
	HelpCircle,
	MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	AvatarFallbackLeaderboard,
	AvatarImageLeaderboard,
	AvatarLeaderboard,
} from '@/components/ui/avatar-leaderboard';
import { cn } from '@/utils';
import { CONTACT_URL, PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '@/constants';
import { useCurrentAccessTokenSelector } from '@/store/slices/auth/session.slice';
import { UserAchievementsType } from '@sportspredict/shared';
import type { NavigationItem, User } from '@/types/layout.types';
import { useLatestAchievementTypeSelector } from '@/store/slices/achievements.slice';
import { SidebarNavItem } from './SidebarNavItem';

interface MobileSidebarProps {
	isOpen: boolean;
	onClose: () => void;
	navigation: NavigationItem[];
	currentPageName: string;
	currentTimezone: string;
	user: User | null | undefined;
	isLoading: boolean;
	userError?: boolean;
	signOut: () => void;
	onAccountClick: () => void;
	onHowToPlayClick: () => void;
	loginPointerProps?: {
		onPointerDown: (e: PointerEvent) => void;
		onClick: (e: MouseEvent) => void;
	};
	isMobile: boolean;
	isRegisteredUser: boolean;
}

export function MobileSidebar({
	isOpen,
	onClose,
	navigation,
	currentPageName,
	currentTimezone,
	user,
	isLoading,
	userError,
	signOut,
	onAccountClick,
	onHowToPlayClick,
	loginPointerProps,
	isMobile,
	isRegisteredUser,
}: MobileSidebarProps) {
	const latestAchievementType = useLatestAchievementTypeSelector();
	const accessToken = useCurrentAccessTokenSelector();
	const closePointerProps = usePointerActivation(onClose);
	const accountPointerProps = usePointerActivation(onAccountClick);
	const howToPlayPointerProps = usePointerActivation(onHowToPlayClick);
	const signOutPointerProps = usePointerActivation(signOut);

	return (
		<>
			{/* Overlay */}
			{isOpen && (
				<div
					className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101]"
					{...closePointerProps}
				/>
			)}

			{/* Sidebar */}
			<div
				className={cn(
					'fixed top-0 right-0 z-[101] h-full w-64 bg-[#171C26] backdrop-blur-xl border-l border-white/10 transform transition-transform duration-300 ease-in-out',
					isOpen ? 'translate-x-0' : 'translate-x-full'
				)}
			>
				<div className="flex flex-col h-full">
					<div className="flex justify-end p-2">
						<Button
							variant="ghost"
							size="icon"
							{...closePointerProps}
							className="text-white hover:bg-white/10"
						>
							<X className="h-6 w-6" />
						</Button>
					</div>

					{isLoading ? (
						<div className="p-4 text-center">
							<div className="animate-pulse bg-white/10 h-12 w-12 rounded-full mx-auto mb-2"></div>
							<div className="animate-pulse bg-white/10 h-4 w-24 mx-auto"></div>
						</div>
					) : userError ? (
						<div className="px-4 pb-4 border-b border-white/10">
							<div className="flex flex-col items-center gap-3">
								<Button {...loginPointerProps} className="w-full bg-blue-600 hover:bg-blue-700">
									Sign In
								</Button>
								<p className="text-xs text-gray-400">Sign in to access all features</p>
							</div>
						</div>
					) : (
						user && (
							<div className="px-4 pb-4 border-b border-white/10">
								<div className="flex items-center gap-3">
									<AvatarLeaderboard
										size={40}
										hasGlowBorder={
											latestAchievementType === UserAchievementsType.LinkedAvatarBorder
										}
									>
										<AvatarImageLeaderboard src={user?.img_variants?.xs} />
										<AvatarFallbackLeaderboard iconSize={24}>
											{user?.full_name?.[0]}
										</AvatarFallbackLeaderboard>
									</AvatarLeaderboard>

									<p className="text-xl font-semibold font-['DM_Sans'] leading-normal text-white truncate max-w-[150px]">
										{user.full_name}
									</p>
								</div>
							</div>
						)
					)}

					<nav className="flex-1 p-4">
						<ul className="space-y-1">
							{navigation.map((item) => {
								const isActive = currentPageName === item.name;

								if (item.name === 'account') {
									if (!isRegisteredUser) return null;
									return (
										<li key={item.name}>
											<button
												type="button"
												className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/10"
												{...accountPointerProps}
											>
												<item.icon className="w-5 h-5 text-[#F7F7F8]" />
												{item.tabName}
											</button>
										</li>
									);
								} else if (item.name === 'howtoplay' && !isMobile) {
									return (
										<li key={item.name}>
											<button
												type="button"
												className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/10"
												{...howToPlayPointerProps}
											>
												<item.icon className="w-5 h-5 text-[#F7F7F8]" />
												{item.tabName}
											</button>
										</li>
									);
								}

								return (
									<SidebarNavItem
										key={item.name}
										item={item}
										isActive={isActive}
										onClose={onClose}
									/>
								);
							})}
						</ul>
					</nav>

					<div className="p-4 text-[#D4D5D8]">
						<div className="-mr-4 mb-4 border-t border-[#2D3A6C]" />

						{/* Social Links */}
						<div className="flex items-center gap-3 px-2 py-3">
							<a
								href="https://discord.gg/JjYR3JJzgc"
								target="_blank"
								rel="noopener noreferrer"
								className="hover:text-white transition-colors"
							>
								<MessageSquare className="w-5 h-5" />
							</a>
							<a
								href="https://www.instagram.com/sportspredictcom/"
								target="_blank"
								rel="noopener noreferrer"
								className="hover:text-white transition-colors"
							>
								<Instagram className="w-5 h-5" />
							</a>
						</div>

						{/* Legal Links */}
						<div className="space-y-1 mt-3 text-[#D4D5D8]">
							<a
								href={TERMS_AND_CONDITIONS_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-2 px-2 py-2 text-sm hover:text-white transition-colors rounded-lg hover:bg-white/5"
							>
								<FileText className="w-4 h-4" />
								Terms of Service
							</a>
							<a
								href={PRIVACY_POLICY_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-2 px-2 py-2 text-sm hover:text-white transition-colors rounded-lg hover:bg-white/5"
							>
								<Shield className="w-4 h-4" />
								Privacy Policy
							</a>
							<a
								href={CONTACT_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-2 px-2 py-2 text-sm hover:text-white transition-colors rounded-lg hover:bg-white/5"
							>
								<HelpCircle className="w-4 h-4" />
								Support
							</a>
						</div>

						{/* Timezone display */}
						<div className="flex items-center gap-2 px-2 py-3 text-sm text-[#D4D5D8]">
							<Clock className="w-4 h-4" />
							<span>Timezone: {currentTimezone}</span>
						</div>

						{(user || accessToken) && isRegisteredUser && (
							<Button
								variant="ghost"
								className="w-full px-2 justify-start hover:bg-white/10 mt-3"
								{...signOutPointerProps}
							>
								<LogOut className="w-5 h-5 mr-1" />
								<div className="text-base font-bold font-['DM_Sans'] leading-tight text-[#F7F7F8]">
									Sign Out
								</div>
							</Button>
						)}
					</div>
				</div>
			</div>
		</>
	);
}
